import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import {
  ActionButton,
  breakpoints,
  ButtonModel,
  DrawerContext,
  Text,
  theme,
  Toaster,
} from '@notino/react-styleguide';

import { Hidden } from '@components/Hidden';
import { useWishlistActionsFactory } from '@components/Universals/WishlistModifier/useWishlistActions';
import { useListById } from '@containers/WishList/hooks/useListById';
import { useWishlistStore } from '@containers/WishList/store';
import { useListsData } from '@containers/WishlistLists/hooks/useListsData';
import { styled } from '@linaria/react';

import { selectionModeMessages } from './messages';

export const useOpenBulkRemoveProductsDrawer = () => {
  const { openDrawer } = DrawerContext.useDrawerContext();
  const { toggleSelectionMode, bulkRemoveProducts } = useWishlistStore(
    (x) => x.actions
  );

  const open = ({ productIds }: { productIds: string[] }) => {
    openDrawer(
      <Content
        productIds={productIds}
        toggleSelectionMode={toggleSelectionMode}
        bulkRemoveProducts={bulkRemoveProducts}
      />,
      {
        title: <Header />,
        hideScrollbar: true,
        initialPosition: 'dynamic',
        showFade: false,
        headerCustomStyles: 'list-menu-drawer-header',
      }
    );
  };

  return { open };
};

const HeaderWrapper = styled.div`
  ${theme.typography.labelLarge}
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (min-width: ${breakpoints.md}) {
    margin-left: 1rem;
  }

  svg {
    cursor: pointer;
  }
`;

const Header = () => {
  return (
    <>
      <Hidden breakpoint="md" style={{ width: 52 }} />
      <HeaderWrapper>
        <FormattedMessage
          {...selectionModeMessages.removeProductsFromListDrawerTitle}
        />
      </HeaderWrapper>
    </>
  );
};

const ContentWrapper = styled.div`
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ContentFooter = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-end;
`;

const ConetntButtons = styled.div`
  width: 100%;
  margin-top: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  > * {
    width: 100%;
  }
`;

const Content = ({
  productIds,
  toggleSelectionMode,
  bulkRemoveProducts,
}: {
  productIds: string[];
  toggleSelectionMode: () => void;
  bulkRemoveProducts: (productIds: string[]) => void;
}) => {
  const { listById } = useListById();
  const { lists } = useListsData();
  const { closeDrawer } = DrawerContext.useDrawerContext();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);

  const { handleWishlistClick } = useWishlistActionsFactory({});

  const handleRemove = async () => {
    setLoading(true);
    setError(false);
    try {
      await handleWishlistClick({
        actionLocation: 'wishlist',
        addToLists: [],
        removeFromLists: [{ id: listById.id }],
        productIds,
        lists,
        interaction: 'remove_button',
      });
      toggleSelectionMode();
      closeDrawer();
      bulkRemoveProducts(productIds);
      Toaster.info({
        content: (
          <FormattedMessage
            {...selectionModeMessages.removedProductsToastText}
            values={{
              listName: listById.name ?? '',
              b: (chunks) => <b>{chunks}</b>,
            }}
          />
        ),
        icon: 'IconRegularCheckmark',
      });
    } catch (err) {
      setError(true);
    }
    setLoading(false);
  };

  return (
    <ContentWrapper>
      <Text typography="bodyRegular" color="text.secondary">
        <FormattedMessage
          {...selectionModeMessages.removeProductsFromListDrawerText}
          values={{ listName: listById.name }}
        />
      </Text>

      <ContentFooter>
        <ConetntButtons>
          <ActionButton
            onClick={handleRemove}
            isLoading={loading}
            error={error}
          >
            <FormattedMessage
              {...selectionModeMessages.removeProductsFromListDrawerYesButton}
            />
          </ActionButton>

          <ActionButton
            buttonStyle={ButtonModel.Styles.secondary}
            onClick={closeDrawer}
          >
            <FormattedMessage
              {...selectionModeMessages.removeProductsFromListDrawerNoButton}
            />
          </ActionButton>
        </ConetntButtons>
      </ContentFooter>
    </ContentWrapper>
  );
};
